import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIButton from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useNavigate } from 'react-router-dom';
import Session from 'utils/Session';

const RAZORPAY = {
    section:   'https://rzp.io/rzp/3Qp9qObO',
    fullTest:  'https://rzp.io/rzp/LKEHKtW',
    unlimited: 'https://rzp.io/rzp/DJV8NEOR',
};

const PLANS = [
    {
        id: 'basic',
        name: 'Free',
        price: 'Free',
        originalPrice: null,
        priceLabel: null,
        subtitle: 'Get started with essential practice',
        features: [
            '1 full mock test',
            'Detailed AI feedback',
            'All 4 IELTS sections',
            'Free study material access',
        ],
        cta: 'Start Free',
        ctaVariant: 'outlined',
        ctaHref: '/practice#practice-by-tests',
    },
    {
        id: 'standard-full',
        name: 'Standard',
        price: '₹99',
        originalPrice: '₹249',
        priceLabel: '/Full test',
        subtitle: 'Serious preparation with detailed insights',
        features: [
            'Detailed AI feedback',
            'Section-wise analysis',
            'Free study material access',
            'Progress tracking dashboard',
            'Band prediction',
        ],
        cta: 'Buy a Test',
        ctaVariant: 'contained',
        ctaHref: RAZORPAY.fullTest,
    },
    {
        id: 'standard-sectional',
        name: 'Standard',
        price: '₹49',
        originalPrice: '₹149',
        priceLabel: '/Sectional test',
        subtitle: 'Serious preparation with detailed insights',
        isSectional: true,
        sectionalNote: 'Includes all as Standard full test',
        sections: ['Writing', 'Listening', 'speaking', 'Reading'],
        cta: 'Buy a Test',
        ctaVariant: 'contained',
        ctaHref: RAZORPAY.section,
    },
    {
        id: 'premium',
        name: 'Premium',
        price: '₹19,999',
        originalPrice: '₹39,999',
        priceLabel: null,
        subtitle: 'Maximum support for guaranteed results',
        features: [
            'Unlimited mock tests',
            '30 days 1 on 1 handholding by expert coach',
            'Section-wise analysis',
            'Free study material access',
            'Progress tracking dashboard',
        ],
        cta: 'Buy a Test',
        ctaVariant: 'contained',
        ctaHref: RAZORPAY.unlimited,
    },
];

const useStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        padding: '64px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            padding: '48px 16px',
        },
    },

    // ── Header ──────────────────────────────────────────
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: theme.palette.shades?.['clr-offWhite'] || '#FCFAFA',
        border: `1px solid ${theme.palette.neutral?.['clr-200'] || '#D3D8DE'}`,
        borderRadius: '50px',
        padding: '5px 14px',
        marginBottom: '18px',
    },

    // ── Card Grid ───────────────────────────────────────
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        width: '100%',
        maxWidth: '1100px',
        [theme.breakpoints.down('lg')]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
        },
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: '1fr',
            maxWidth: '420px',
        },
    },

    // ── Card ────────────────────────────────────────────
    card: {
        backgroundColor: '#1E2243',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: '14px',
        padding: '28px 24px',
        display: 'flex',
        flexDirection: 'column',
    },

    // ── CTA ─────────────────────────────────────────────
    ctaOutlined: {
        ...theme.typography['body02-bold'],
        color: '#EE634E',
        border: '1.5px solid #EE634E',
        borderRadius: '50px',
        padding: '10px 28px',
        textTransform: 'none',
        width: '100%',
        '&:hover': {
            backgroundColor: 'rgba(238,99,78,0.1)',
            border: '1.5px solid #EE634E',
        },
    },
    ctaContained: {
        ...theme.typography['body02-bold'],
        backgroundColor: '#EE634E',
        color: theme.palette.shades?.['clr-white-900'] || '#FFFFFF',
        borderRadius: '50px',
        padding: '10px 28px',
        textTransform: 'none',
        width: '100%',
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: '#d9563f',
            boxShadow: 'none',
        },
    },
}));

function PricingCard({ plan }) {
    const classes = useStyles();
    const navigate = useNavigate();

    const handleCta = (e) => {
        if (plan.ctaHref.startsWith('http')) {
            if (!Session.isLoggedIn()) {
                e.preventDefault();
                navigate('/login');
            }
        }
    };

    return (
        <Box className={classes.card}>

            {/* Plan name */}
            <Typography
                component="div"
                variant="h6-bold"
                sx={{ color: '#FFFFFF', fontStyle: 'italic', mb: '8px' }}
            >
                {plan.name}
            </Typography>

            {/* Price */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                {plan.originalPrice && !plan.isSectional && (
                    <Typography
                        component="span"
                        variant="body01-regular"
                        sx={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'line-through' }}
                    >
                        {plan.originalPrice}
                    </Typography>
                )}
                <Typography
                    component="span"
                    variant="h3-bold"
                    sx={{ color: '#FFFFFF', fontStyle: 'italic' }}
                >
                    {plan.price}
                </Typography>
                {plan.priceLabel && (
                    <Typography
                        component="span"
                        variant="body02-regular"
                        sx={{ color: '#E6E6E6' }}
                    >
                        {plan.priceLabel}
                    </Typography>
                )}
            </Box>

            {/* Sectional original price in bordered box */}
            {plan.isSectional && plan.originalPrice && (
                <Box
                    sx={{
                        border: '1px solid rgba(255,255,255,0.3)',
                        borderRadius: '4px',
                        padding: '4px 16px',
                        mt: '8px',
                        mb: '4px',
                        width: 'fit-content',
                    }}
                >
                    <Typography
                        component="span"
                        variant="body01-medium"
                        sx={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through' }}
                    >
                        {plan.originalPrice}
                    </Typography>
                </Box>
            )}

            {/* Subtitle */}
            <Typography
                component="p"
                variant="body02-regular"
                sx={{
                    color: '#E6E6E6',
                    fontStyle: 'italic',
                    lineHeight: 1.6,
                    mt: '16px',
                    mb: '20px',
                }}
            >
                {plan.subtitle}
            </Typography>

            {/* Sectional card — sections list */}
            {plan.isSectional ? (
                <Box sx={{ flex: 1, mb: '28px' }}>
                    <Typography
                        component="div"
                        variant="body02-bold"
                        sx={{ color: '#FFFFFF', mb: '12px' }}
                    >
                        {plan.sectionalNote}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {plan.sections.map(section => (
                            <Box key={section} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <RadioButtonUncheckedIcon
                                    sx={{ color: '#E6E6E6', fontSize: '1rem', flexShrink: 0 }}
                                />
                                <Typography
                                    component="span"
                                    variant="body02-semiBold"
                                    sx={{ color: '#FFFFFF' }}
                                >
                                    {section}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ) : (
                <>
                    {/* Includes label */}
                    <Typography
                        component="div"
                        variant="body02-bold"
                        sx={{ color: '#FFFFFF', mb: '12px' }}
                    >
                        Includes:
                    </Typography>

                    {/* Feature list */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mb: '28px', flex: 1 }}>
                        {plan.features.map(feature => (
                            <Box key={feature} sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                <CheckIcon
                                    sx={{ color: '#EE634E', fontSize: '1rem', mt: '2px', flexShrink: 0 }}
                                />
                                <Typography
                                    component="span"
                                    variant="body02-regular"
                                    sx={{ color: '#E6E6E6', lineHeight: 1.5 }}
                                >
                                    {feature}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}

            {/* CTA */}
            <MUIButton
                variant={plan.ctaVariant}
                disableElevation
                component="a"
                href={plan.ctaHref}
                target="_self"
                onClick={handleCta}
                className={plan.ctaVariant === 'outlined' ? classes.ctaOutlined : classes.ctaContained}
            >
                {plan.cta}
            </MUIButton>

        </Box>
    );
}

function Pricing() {
    const classes = useStyles();

    return (
        <Box id="pricing" className={classes.root}>

            {/* Badge */}
            <Box className={classes.badge}>
                <Typography component="span" sx={{ color: '#EE634E', fontSize: '0.8rem' }}>
                    ✳
                </Typography>
                <Typography
                    component="span"
                    variant="body02-semiBold"
                    sx={{ color: '#EE634E', letterSpacing: '0.04em' }}
                >
                    Pricing
                </Typography>
            </Box>

            {/* Heading */}
            <Typography
                component="h2"
                variant="h2-bold"
                color="#ffffff"
                sx={{ textAlign: 'center', mb: '14px', maxWidth: '480px' }}
            >
                Choose Your Path to a Higher Band
            </Typography>

            {/* Subtitle */}
            <Typography
                component="p"
                variant="body01-regular"
                color="neutral.clr-500"
                sx={{ textAlign: 'center', maxWidth: '440px', mb: '20px', lineHeight: 1.65 }}
            >
                All plans include free study material. Upgrade anytime as your needs grow.
            </Typography>

            {/* Free note */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '48px' }}>
                <Typography component="span" variant="body02-regular" color="neutral.clr-500">
                    ✓
                </Typography>
                <Typography component="span" variant="body02-regular" color="neutral.clr-500">
                    Free study material included in all plans
                </Typography>
            </Box>

            {/* Pricing cards */}
            <Box className={classes.grid}>
                {PLANS.map(plan => (
                    <PricingCard key={plan.id} plan={plan} />
                ))}
            </Box>

        </Box>
    );
}

export default Pricing;
